<template>
    <div class="pa-4">
        <div class="text-center mb-4">
            <v-icon size="36" color="primary" class="mb-2">mdi-clock-outline</v-icon>
            <h3 class="text-subtitle-1 font-weight-bold">Schedule</h3>
            <p class="text-body-2 text-medium-emphasis">Configure when this monitoring rule should be active</p>
        </div>

        <!-- Master toggle: most rules are always-on, so this leads and defaults
             on; turning it off reveals the detailed scheduling options. -->
        <v-card variant="outlined" rounded="lg" class="pa-3 mb-3">
            <v-switch v-model="formData['all_times_enabled']" color="primary" density="comfortable"
                      label="Always active" hide-details @update:model-value="onAlwaysActive"/>
            <p class="text-caption text-medium-emphasis mb-0">
                {{ formData['all_times_enabled']
                    ? 'Active 24/7. Turn this off to limit the alert to certain dates, days or times.'
                    : 'Choose exactly when this alert is active below.' }}
            </p>
        </v-card>

        <v-expand-transition>
        <v-row v-if="!formData['all_times_enabled']">
            <v-col cols="12" sm="5">
                <v-card variant="flat" rounded="lg" class="pa-3 bg-grey-lighten-4 text-grey-darken-3">
                    <p class="text-body-2 font-weight-medium mb-2">Active periods</p>
                    <v-switch v-model="formData['start_date_enabled']" color="primary" density="compact"
                              label="Start date" hide-details class="mb-1"
                              @update:model-value="checkSwitches"/>
                    <v-switch v-model="formData['end_date_enabled']" color="primary" density="compact"
                              label="End date" hide-details class="mb-1"
                              @update:model-value="checkSwitches"/>
                    <v-switch v-model="formData['weekdays_enabled']" color="primary" density="compact"
                              label="Specific weekdays" hide-details class="mb-1"
                              @update:model-value="checkSwitches"/>
                    <v-switch v-model="formData['months_enabled']" color="primary" density="compact"
                              label="Specific months" hide-details class="mb-1"
                              @update:model-value="checkSwitches"/>
                    <v-switch v-model="formData['days_enabled']" color="primary" density="compact"
                              label="Specific days" hide-details class="mb-1"
                              @update:model-value="checkSwitches"/>
                    <v-switch v-model="formData['hours_enabled']" color="primary" density="compact"
                              label="Specific hours" hide-details class="mb-1"
                              @update:model-value="checkSwitches"/>
                    <v-switch v-model="formData['tod_enabled']" color="primary" density="compact"
                              label="Time of day range" hide-details class="mb-1"
                              @update:model-value="checkSwitches"/>
                    <v-switch v-model="formData['interval_enabled']" color="primary" density="compact"
                              label="Repeat interval" hide-details
                              @update:model-value="checkSwitches"/>
                </v-card>
            </v-col>
            <v-col cols="12" sm="7">
                <v-text-field v-model="formData['start_date']" :disabled="!formData['start_date_enabled']"
                              density="compact" label="Start Date" type="date" variant="outlined" class="mb-1"/>

                <v-text-field v-model="formData['end_date']" :disabled="!formData['end_date_enabled']"
                              density="compact" label="End Date" type="date" variant="outlined" class="mb-1"/>

                <v-select v-model="formData['weekdays']" :disabled="!formData['weekdays_enabled']" :items="weekdays"
                          density="compact" item-title="title" item-value="value" label="Weekdays"
                          multiple variant="outlined" class="mb-1"/>

                <v-select v-model="formData['months']" :disabled="!formData['months_enabled']" :items="months"
                          density="compact" item-title="title" item-value="value" label="Months"
                          multiple variant="outlined" class="mb-1"/>

                <v-select v-model="formData['days']" :disabled="!formData['days_enabled']" :items="days"
                          density="compact" label="Days of Month" multiple variant="outlined" class="mb-1"/>

                <v-select v-model="formData['hours']" :disabled="!formData['hours_enabled']" :items="hours"
                          density="compact" label="Hours" multiple variant="outlined" class="mb-1"/>

                <v-row dense>
                    <v-col cols="6">
                        <v-text-field v-model="formData['tod_start']" :disabled="!formData['tod_enabled']"
                                      density="compact" label="From (time)" type="time" variant="outlined"/>
                    </v-col>
                    <v-col cols="6">
                        <v-text-field v-model="formData['tod_end']" :disabled="!formData['tod_enabled']"
                                      density="compact" label="To (time)" type="time" variant="outlined"/>
                    </v-col>
                </v-row>

                <v-row dense>
                    <v-col cols="6">
                        <v-text-field v-model="formData['repeat_interval']" :disabled="!formData['interval_enabled']"
                                      density="compact" label="Repeat Every" type="number" variant="outlined"/>
                    </v-col>
                    <v-col cols="6">
                        <v-select v-model="formData['repeat_interval_type']" :disabled="!formData['interval_enabled']"
                                  :items="[{value:0,title:'Seconds'},{value:1,title:'Minutes'},{value:2,title:'Hours'},{value:3,title:'Days'},{value:4,title:'Months'}]"
                                  density="compact" item-title="title" item-value="value" label="Interval Unit"
                                  variant="outlined"/>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        </v-expand-transition>
    </div>
</template>

<script setup>
import {reactive, watch, onMounted} from "vue";

const props = defineProps({
    data: Object,
});
const emits = defineEmits(['setSchedule']);
const weekdays = [{value: 1, title: 'Monday'}, {value: 2, title: 'Tuesday'},
    {value: 3, title: 'Wednesday'}, {value: 4, title: 'Thursday'}, {value: 5, title: 'Friday'},
    {value: 6, title: 'Saturday'}, {value: 7, title: 'Sunday'}];
const months = [{value: 1, title: 'January'}, {value: 2, title: 'February'},
    {value: 3, title: 'March'}, {value: 4, title: 'April'}, {value: 5, title: 'May'},
    {value: 6, title: 'June'}, {value: 7, title: 'July'}, {value: 8, title: 'August'},
    {value: 9, title: 'September'}, {value: 10, title: 'October'}, {value: 11, title: 'November'},
    {value: 12, title: 'December'}];
const days = Array.from({length: 31}, (_, i) => i + 1);
const hours = Array.from({length: 24}, (_, i) => i);

const toggleFields = {
    start_date_enabled: ['start_date'],
    end_date_enabled: ['end_date'],
    weekdays_enabled: ['weekdays'],
    months_enabled: ['months'],
    days_enabled: ['days'],
    hours_enabled: ['hours'],
    tod_enabled: ['tod_start', 'tod_end'],
    interval_enabled: ['repeat_interval', 'repeat_interval_type'],
};

const formatTime = (time) => {
    if (!time) return null;
    const parts = time.split(':');
    return parts.length > 1 ? `${parts[0]}:${parts[1]}` : time;
};

const checkSwitches = () => {
    formData['all_times_enabled'] = !Object.keys(toggleFields).some(key => formData[key]);
};

// Master "Always active": turning it on clears every specific so the rule is
// truly always-on (and the detail section collapses).
const onAlwaysActive = (val) => {
    if (val) {
        Object.keys(toggleFields).forEach((key) => { formData[key] = false; });
    }
};

const formData = reactive({
    id: props.data?.id ?? null,
    all_times_enabled: !!(props.data?.all_times_enabled ?? true),
    start_date_enabled: !!props.data?.start_date_enabled,
    end_date_enabled: !!props.data?.end_date_enabled,
    weekdays_enabled: !!props.data?.weekdays_enabled,
    months_enabled: !!props.data?.months_enabled,
    days_enabled: !!props.data?.days_enabled,
    hours_enabled: !!props.data?.hours_enabled,
    tod_enabled: !!props.data?.tod_enabled,
    interval_enabled: !!props.data?.interval_enabled,
    repeat_interval_type: props.data?.repeat_interval_type,
    repeat_interval: props.data?.repeat_interval,
    start_date: props.data?.start_date,
    end_date: props.data?.end_date,
    months: props.data?.months,
    days: props.data?.days,
    hours: props.data?.hours,
    weekdays: props.data?.weekdays,
    tod_start: formatTime(props.data?.tod_start),
    tod_end: formatTime(props.data?.tod_end),
});

onMounted(() => {
    emits('setSchedule', formData);
});

watch(formData, () => {
    emits('setSchedule', formData);
}, {deep: true});
</script>
